import { useId } from "react";
import type { HTMLInputTypeAttribute } from "react";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Input } from "@/components/ui/input";
import type { FieldSize } from "@/components/ui/field-chrome";
import { Panel } from "@/components/ui/Panel";
import type {
  KeyContactFields,
  KeyContactResponse,
  KeyContactUpdate,
} from "@patent-studio/api-types";

/** One labelled line of a client's form, in the house modal's sentence-case label. */
export function TextField({
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  optional = false,
  size = "roomy",
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  type?: HTMLInputTypeAttribute;
  optional?: boolean;
  /** `roomy` in a modal; the default in a popover, where the fields share a narrow panel. */
  size?: FieldSize;
}) {
  const inputId = useId();
  return (
    <div>
      <FieldLabel htmlFor={inputId} look="sentence" optional={optional}>
        {label}
      </FieldLabel>
      <Input
        id={inputId}
        type={type}
        size={size}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

/** The key contact as the form holds it: four lines, any of which may be empty. */
export interface ContactDraft {
  name: string;
  job_title: string;
  email: string;
  phone: string;
}

export const NO_CONTACT: ContactDraft = {
  name: "",
  job_title: "",
  email: "",
  phone: "",
};

const CONTACT_KEYS = ["name", "job_title", "email", "phone"] as const;

export function contactDraftOf(contact: KeyContactResponse): ContactDraft {
  return {
    name: contact.name ?? "",
    job_title: contact.job_title ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
  };
}

/** A detail as it is sent: trimmed, or null where only space was typed. */
function sent(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The details a new client's contact is given with; one left empty is not sent. */
export function givenContact(
  draft: ContactDraft,
): KeyContactFields | undefined {
  const given = Object.fromEntries(
    CONTACT_KEYS.flatMap((key) => {
      const value = sent(draft[key]);
      return value === null ? [] : [[key, value]];
    }),
  );
  return Object.keys(given).length === 0 ? undefined : given;
}

/** The details an edit changed, and nothing else; one emptied is sent as null to clear it. */
export function contactChanges(
  contact: KeyContactResponse,
  draft: ContactDraft,
): KeyContactUpdate | undefined {
  const changed = Object.fromEntries(
    CONTACT_KEYS.flatMap((key) => {
      const value = sent(draft[key]);
      return value === contact[key] ? [] : [[key, value]];
    }),
  );
  return Object.keys(changed).length === 0 ? undefined : changed;
}

/**
 * The key contact's four details, as a section of the client's form.
 *
 * Set apart on a ground of its own, under a heading marked optional the way the
 * logo's label is, so the attorney reads it as one thing that can be left for
 * later rather than four more fields a client needs. Each detail is optional
 * alone, so none of them says so again.
 */
export function KeyContactSection({
  value,
  onChange,
}: {
  value: ContactDraft;
  onChange: (value: ContactDraft) => void;
}) {
  const captionId = useId();
  const set = (key: keyof ContactDraft) => (typed: string) => {
    onChange({ ...value, [key]: typed });
  };
  return (
    <Panel tone="neutral" pad="snug">
      <fieldset aria-describedby={captionId} className="min-w-0">
        <legend className="text-[12.5px] font-bold text-foreground">
          Key contact{" "}
          <span className="font-normal text-subtle-foreground">(optional)</span>
        </legend>
        <p
          id={captionId}
          className="mt-1 text-[11.5px] leading-[1.45] text-muted-foreground"
        >
          The person you deal with at this client. Add what you know now; the
          rest can be filled in later.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <TextField
            label="Name"
            placeholder="e.g. Ada Park"
            value={value.name}
            onChange={set("name")}
          />
          <TextField
            label="Job title"
            placeholder="e.g. Head of IP"
            value={value.job_title}
            onChange={set("job_title")}
          />
          <TextField
            label="Email"
            type="email"
            placeholder="name@company.com"
            value={value.email}
            onChange={set("email")}
          />
          <TextField
            label="Contact number"
            type="tel"
            placeholder="e.g. 0100 000 000"
            value={value.phone}
            onChange={set("phone")}
          />
        </div>
      </fieldset>
    </Panel>
  );
}
